import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { ArrowLeft, Camera, X, Check, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EditListing = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [categories, setCategories] = useState([]);
  const [locations, setLocations] = useState([]);
  const [existingImages, setExistingImages] = useState([]);
  const [newImages, setNewImages] = useState([]);
  const [uploadingImages, setUploadingImages] = useState(false);
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    price: '',
    location_id: '',
    condition: 'used',
    seller_name: '',
    seller_phone: '',
    seller_whatsapp: '',
  });

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        navigate('/auth');
        return;
      }

      const [listingRes, categoriesRes, locationsRes] = await Promise.all([
        axios.get(`${API}/listings/${id}`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/categories`),
        axios.get(`${API}/locations`)
      ]);

      const listing = listingRes.data;
      setFormData({
        title: listing.title || '',
        description: listing.description || '',
        price: listing.price?.toString() || '',
        location_id: listing.location_id || '',
        condition: listing.condition || 'used',
        seller_name: listing.seller_name || '',
        seller_phone: listing.seller_phone || '',
        seller_whatsapp: listing.seller_whatsapp || '',
      });
      setExistingImages(listing.images || []);
      setCategories(categoriesRes.data);
      setLocations(locationsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      alert('Error loading listing');
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  };

  const handleImageSelect = (e) => {
    const files = Array.from(e.target.files);
    const totalImages = existingImages.length + newImages.length;
    const remainingSlots = 6 - totalImages;
    const filesToAdd = files.slice(0, remainingSlots);
    setNewImages([...newImages, ...filesToAdd]);
  };

  const removeExistingImage = (index) => {
    setExistingImages(existingImages.filter((_, i) => i !== index));
  };

  const removeNewImage = (index) => {
    setNewImages(newImages.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const token = localStorage.getItem('token');

      // Update listing details
      await axios.put(`${API}/listings/${id}`, {
        ...formData,
        price: parseFloat(formData.price),
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Upload new images if any
      if (newImages.length > 0) {
        setUploadingImages(true);
        for (const image of newImages) {
          const formDataImg = new FormData();
          formDataImg.append('file', image);
          try {
            await axios.post(`${API}/listings/${id}/images`, formDataImg, {
              headers: { 
                Authorization: `Bearer ${token}`,
                'Content-Type': 'multipart/form-data'
              }
            });
          } catch (imgErr) {
            console.error('Error uploading image:', imgErr);
          }
        }
        setUploadingImages(false);
      }

      navigate('/dashboard');
    } catch (error) {
      console.error('Error updating listing:', error);
      alert(error.response?.data?.detail || 'Error updating listing');
    } finally {
      setSaving(false);
      setUploadingImages(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F8F9FA] flex items-center justify-center">
        <div className="w-8 h-8 border-3 border-[#E31C25] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8F9FA] pb-24 md:pb-8">
      {/* Header */}
      <div className="sticky top-0 z-40 bg-white border-b border-gray-100">
        <div className="max-w-2xl mx-auto px-4 py-3 flex items-center justify-between">
          <button onClick={() => navigate(-1)} className="p-2 -ml-2" data-testid="back-btn">
            <ArrowLeft className="w-6 h-6 text-gray-900" />
          </button>
          <h1 className="font-bold text-gray-900">Edit Listing</h1>
          <div className="w-10"></div>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="max-w-2xl mx-auto px-4 py-6">
        {/* Photos Section */}
        <div className="bg-white rounded-xl border border-gray-100 p-5 mb-4">
          <h2 className="text-lg font-bold text-gray-900 mb-4">Photos</h2>
          <div className="grid grid-cols-3 gap-3">
            {/* Existing Images */}
            {existingImages.map((image, index) => (
              <div key={`existing-${index}`} className="relative aspect-square bg-gray-100 rounded-xl overflow-hidden">
                <img
                  src={image.image_url}
                  alt={`Image ${index + 1}`}
                  className="w-full h-full object-cover"
                />
                <button
                  type="button"
                  onClick={() => removeExistingImage(index)}
                  className="absolute top-2 right-2 w-6 h-6 bg-black/60 text-white rounded-full flex items-center justify-center"
                >
                  <X className="w-4 h-4" />
                </button>
                {index === 0 && (
                  <span className="absolute bottom-2 left-2 px-2 py-1 bg-black/60 text-white text-xs rounded">
                    Cover
                  </span>
                )}
              </div>
            ))}

            {/* New Images */}
            {newImages.map((image, index) => (
              <div key={`new-${index}`} className="relative aspect-square bg-gray-100 rounded-xl overflow-hidden">
                <img
                  src={URL.createObjectURL(image)}
                  alt={`New ${index + 1}`}
                  className="w-full h-full object-cover"
                />
                <button
                  type="button"
                  onClick={() => removeNewImage(index)}
                  className="absolute top-2 right-2 w-6 h-6 bg-black/60 text-white rounded-full flex items-center justify-center"
                >
                  <X className="w-4 h-4" />
                </button>
                <span className="absolute bottom-2 left-2 px-2 py-1 bg-green-600 text-white text-xs rounded">
                  New
                </span>
              </div>
            ))}

            {/* Add Photo Button */}
            {existingImages.length + newImages.length < 6 && (
              <label className="aspect-square bg-gray-50 border-2 border-dashed border-gray-200 rounded-xl flex flex-col items-center justify-center cursor-pointer hover:bg-gray-100 transition-colors">
                <Camera className="w-8 h-8 text-gray-400 mb-2" />
                <span className="text-sm text-gray-500">Add Photo</span>
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleImageSelect}
                  className="hidden"
                  data-testid="photo-upload"
                />
              </label>
            )}
          </div>
        </div>

        {/* Details Section */}
        <div className="bg-white rounded-xl border border-gray-100 p-5 mb-4 space-y-4">
          <h2 className="text-lg font-bold text-gray-900">Details</h2>

          <div>
            <label className="block text-sm font-semibold text-gray-900 mb-2">Title *</label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              className="w-full px-4 py-3 border border-gray-200 rounded-lg outline-none focus:border-[#E31C25]"
              required
              data-testid="edit-title"
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-900 mb-2">Description *</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows={4}
              className="w-full px-4 py-3 border border-gray-200 rounded-lg outline-none focus:border-[#E31C25]"
              required
              data-testid="edit-description"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold text-gray-900 mb-2">Price (AED) *</label>
              <input
                type="number"
                value={formData.price}
                onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                className="w-full px-4 py-3 border border-gray-200 rounded-lg outline-none focus:border-[#E31C25]"
                required
                data-testid="edit-price"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-900 mb-2">Location *</label>
              <select
                value={formData.location_id}
                onChange={(e) => setFormData({ ...formData, location_id: e.target.value })}
                className="w-full px-4 py-3 border border-gray-200 rounded-lg outline-none focus:border-[#E31C25]"
                required
                data-testid="edit-location"
              >
                <option value="">Select</option>
                {locations.map((loc) => (
                  <option key={loc.id} value={loc.id}>{loc.city}</option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-900 mb-2">Condition</label>
            <div className="flex gap-3">
              {['new', 'used'].map((cond) => (
                <button
                  key={cond}
                  type="button"
                  onClick={() => setFormData({ ...formData, condition: cond })}
                  className={`flex-1 px-4 py-3 rounded-lg border-2 transition-all capitalize ${
                    formData.condition === cond
                      ? 'border-[#E31C25] bg-red-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                  data-testid={`edit-condition-${cond}`}
                >
                  {cond}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Contact Section */}
        <div className="bg-white rounded-xl border border-gray-100 p-5 mb-6 space-y-4">
          <h2 className="text-lg font-bold text-gray-900">Contact Info</h2>

          <div>
            <label className="block text-sm font-semibold text-gray-900 mb-2">Your Name</label>
            <input
              type="text"
              value={formData.seller_name}
              onChange={(e) => setFormData({ ...formData, seller_name: e.target.value })}
              className="w-full px-4 py-3 border border-gray-200 rounded-lg outline-none focus:border-[#E31C25]"
              data-testid="edit-seller-name"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold text-gray-900 mb-2">Phone</label>
              <input
                type="tel"
                value={formData.seller_phone}
                onChange={(e) => setFormData({ ...formData, seller_phone: e.target.value })}
                className="w-full px-4 py-3 border border-gray-200 rounded-lg outline-none focus:border-[#E31C25]"
                data-testid="edit-seller-phone"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-900 mb-2">WhatsApp</label>
              <input
                type="tel"
                value={formData.seller_whatsapp}
                onChange={(e) => setFormData({ ...formData, seller_whatsapp: e.target.value })}
                className="w-full px-4 py-3 border border-gray-200 rounded-lg outline-none focus:border-[#E31C25]"
                data-testid="edit-seller-whatsapp"
              />
            </div>
          </div>
        </div>

        {/* Submit Button */}
        <button
          type="submit"
          disabled={saving}
          className="w-full py-3 bg-[#E31C25] text-white rounded-lg font-semibold hover:bg-[#C41820] disabled:bg-gray-300 transition-colors flex items-center justify-center gap-2"
          data-testid="save-listing-btn"
        >
          {saving ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin" />
              <span>{uploadingImages ? 'Uploading Photos...' : 'Saving...'}</span>
            </>
          ) : (
            <>
              <Check className="w-5 h-5" />
              <span>Save Changes</span>
            </>
          )}
        </button>
      </form>
    </div>
  );
};

export default EditListing;
